import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, AreaChart, Area } from 'recharts';
import axios from 'axios';
import { useCookies } from 'react-cookie';
import config from './Componentes/config';
import './ccss/Dashboard.css';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

const Dashboard = () => {
    const [cookies] = useCookies(['idEmpresa', 'NombreEmpresa', 'selectedCard']);
    const idEmpresa = cookies.idEmpresa;
    const nombreEmpresa = cookies.NombreEmpresa;

    // Estado para el filtro de mes - inicializado con validación para agosto 2025 mínimo
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1;
    const currentYear = currentDate.getFullYear();

    // Si es 2025 y el mes actual es menor a agosto, inicializar en agosto
    const initialMonth = (currentYear === 2025 && currentMonth < 8) ? 8 : currentMonth;
    const initialYear = currentYear < 2025 ? 2025 : currentYear;

    const [selectedMonth, setSelectedMonth] = useState(initialMonth);
    const [selectedYear, setSelectedYear] = useState(initialYear);

    const [dashboardData, setDashboardData] = useState({
        trabajadoresStats: {},
        asistenciaStats: {},
        permisosVacaciones: {},
        trabajadoresPorEmpresa: [],
        asistenciaUltimosDias: [],
        actividadMensual: [],
        resumenGeneral: {},
        compensadosMes: [],
        cotizacionesStats: {},
        cumpleanosMes: { cumpleanos: [], total: 0 }
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (idEmpresa) {
            fetchDashboardData();
        } else {
            setError('No se ha seleccionado una empresa. Por favor, seleccione una empresa para ver el dashboard.');
            setLoading(false);
        }
    }, [idEmpresa, selectedMonth, selectedYear]);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            setError(null);

            const [
                trabajadoresStats,
                asistenciaStats,
                permisosVacaciones,
                asistenciaUltimosDias,
                actividadMensual,
                compensadosMes,
                cotizacionesStats,
                proximoCumpleanos
            ] = await Promise.all([
                axios.get(`${config.API_URL}/api/dashboard/trabajadores-stats?idEmpresa=${idEmpresa}&mes=${selectedMonth}&año=${selectedYear}`),
                axios.get(`${config.API_URL}/api/dashboard/asistencia-stats?idEmpresa=${idEmpresa}&mes=${selectedMonth}&año=${selectedYear}`),
                axios.get(`${config.API_URL}/api/dashboard/permisos-vacaciones?idEmpresa=${idEmpresa}&mes=${selectedMonth}&año=${selectedYear}`),
                axios.get(`${config.API_URL}/api/dashboard/asistencia-ultimos-dias?idEmpresa=${idEmpresa}&mes=${selectedMonth}&año=${selectedYear}`),
                axios.get(`${config.API_URL}/api/dashboard/actividad-mensual?idEmpresa=${idEmpresa}&mes=${selectedMonth}&año=${selectedYear}`),
                axios.get(`${config.API_URL}/api/dashboard/compensados-mes?idEmpresa=${idEmpresa}&mes=${selectedMonth}&año=${selectedYear}`),
                axios.get(`${config.API_URL}/api/dashboard/cotizaciones-stats?idEmpresa=${idEmpresa}&mes=${selectedMonth}&año=${selectedYear}`),
                axios.get(`${config.API_URL}/api/dashboard/cumpleanos-mes?idEmpresa=${idEmpresa}&mes=${selectedMonth}&ano=${selectedYear}`)
            ]);

            setDashboardData({
                trabajadoresStats: trabajadoresStats.data || {},
                asistenciaStats: asistenciaStats.data || {},
                permisosVacaciones: permisosVacaciones.data || {},
                trabajadoresPorEmpresa: [], // No necesitamos este gráfico si filtramos por empresa
                asistenciaUltimosDias: Array.isArray(asistenciaUltimosDias.data) ? asistenciaUltimosDias.data : [],
                actividadMensual: Array.isArray(actividadMensual.data) ? actividadMensual.data : [],
                resumenGeneral: {},
                compensadosMes: Array.isArray(compensadosMes.data) ? compensadosMes.data : [],
                cotizacionesStats: cotizacionesStats.data || {},
                cumpleanosMes: proximoCumpleanos.data || { cumpleanos: [], total: 0 }
            });
        } catch (err) {
            console.error('Error fetching dashboard data:', err);

            // Establecer datos por defecto en caso de error para evitar crashes
            setDashboardData({
                trabajadoresStats: {},
                asistenciaStats: {},
                permisosVacaciones: {},
                trabajadoresPorEmpresa: [],
                asistenciaUltimosDias: [],
                actividadMensual: [],
                resumenGeneral: {},
                compensadosMes: [],
                cotizacionesStats: {},
                cumpleanosMes: { cumpleanos: [], total: 0 }
            });

            setError('Error al cargar los datos del dashboard. Verifique que el servidor esté funcionando.');
        } finally {
            setLoading(false);
        }
    };

    const KPICard = ({ title, value, subtitle, icon, color, prefix = "", suffix = "" }) => (
        <motion.div
            className={`kpi-card ${color}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            whileHover={{ scale: 1.02 }}
        >
            <div className="kpi-header">
                <div className="kpi-icon">
                    <i className={icon}></i>
                </div>
                <div className="kpi-info">
                    <h3>{title}</h3>
                    <div className="kpi-value">
                        {prefix}
                        <CountUp end={value || 0} duration={2} />
                        {suffix}
                    </div>
                    {subtitle && <p className="kpi-subtitle">{subtitle}</p>}
                </div>
            </div>
        </motion.div>
    );

    if (loading) {
        return (
            <div className="dashboard-loading">
                <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Cargando...</span>
                </div>
                <p>Cargando dashboard...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashboard-error">
                <div className="alert alert-danger" role="alert">
                    <i className="fas fa-exclamation-triangle"></i>
                    <strong>Error:</strong> {error}
                    <button
                        className="btn btn-primary mt-3"
                        onClick={fetchDashboardData}
                    >
                        <i className="fas fa-sync-alt"></i> Reintentar
                    </button>
                </div>
            </div>
        );
    }

    const {
        trabajadoresStats,
        asistenciaStats,
        permisosVacaciones,
        asistenciaUltimosDias,
        actividadMensual,
        compensadosMes,
        cotizacionesStats,
        cumpleanosMes
    } = dashboardData;

    return (
        <div className="dashboard-container">
            {/* Header */}
            <div className="d-sm-flex align-items-center justify-content-between mb-4">
                <motion.h1
                    className="h3 mb-0 text-gray-800"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <i className="fas fa-chart-line"></i> Dashboard {nombreEmpresa || 'ERP - IDAFA'}
                </motion.h1>
                <motion.div
                    className="header-filters"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                >
                    <div className="filter-group">
                        <label htmlFor="month-select">
                            Mes:
                            {selectedYear === 2025 && (
                                <small className="text-muted ml-1" title="Solo disponible desde Agosto 2025">
                                    <i className="fas fa-info-circle"></i>
                                </small>
                            )}
                        </label>
                        <select
                            id="month-select"
                            className="form-control form-control-sm month-selector"
                            value={selectedMonth}
                            onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                            title={selectedYear === 2025 ? "Solo disponible desde Agosto 2025" : "Seleccionar mes"}
                        >
                            <option value={1} disabled={selectedYear === 2025}>
                                {selectedYear === 2025 ? "Enero (No disponible)" : "Enero"}
                            </option>
                            <option value={2} disabled={selectedYear === 2025}>
                                {selectedYear === 2025 ? "Febrero (No disponible)" : "Febrero"}
                            </option>
                            <option value={3} disabled={selectedYear === 2025}>
                                {selectedYear === 2025 ? "Marzo (No disponible)" : "Marzo"}
                            </option>
                            <option value={4} disabled={selectedYear === 2025}>
                                {selectedYear === 2025 ? "Abril (No disponible)" : "Abril"}
                            </option>
                            <option value={5} disabled={selectedYear === 2025}>
                                {selectedYear === 2025 ? "Mayo (No disponible)" : "Mayo"}
                            </option>
                            <option value={6} disabled={selectedYear === 2025}>
                                {selectedYear === 2025 ? "Junio (No disponible)" : "Junio"}
                            </option>
                            <option value={7} disabled={selectedYear === 2025}>
                                {selectedYear === 2025 ? "Julio (No disponible)" : "Julio"}
                            </option>
                            <option value={8}>Agosto</option>
                            <option value={9}>Septiembre</option>
                            <option value={10}>Octubre</option>
                            <option value={11}>Noviembre</option>
                            <option value={12}>Diciembre</option>
                        </select>
                    </div>
                    <div className="filter-group">
                        <label htmlFor="year-select">Año:</label>
                        <select
                            id="year-select"
                            className="form-control form-control-sm"
                            value={selectedYear}
                            onChange={(e) => {
                                const newYear = parseInt(e.target.value);
                                setSelectedYear(newYear);
                                // Si cambia a 2025 y el mes seleccionado es menor a agosto, cambiar a agosto
                                if (newYear === 2025 && selectedMonth < 8) {
                                    setSelectedMonth(8);
                                }
                            }}
                        >
                            <option value={2025}>2025</option>
                            <option value={2026}>2026</option>
                            <option value={2027}>2027</option>
                            <option value={2028}>2028</option>
                        </select>
                    </div>
                </motion.div>
            </div>

            {/* KPIs Row 1 - Principales */}
            <div className="kpi-grid">
                <KPICard
                    title="Trabajadores Activos"
                    value={trabajadoresStats.trabajadores_activos || 0}
                    subtitle={`${trabajadoresStats.total_trabajadores || 0} total`}
                    icon="fas fa-users"
                    color="blue"
                />
                <KPICard
                    title="Horas Promedio"
                    value={parseFloat(asistenciaStats.promedio_horas_trabajadas || 0).toFixed(1)}
                    subtitle="Horas trabajadas/día"
                    icon="fas fa-clock"
                    color="green"
                    suffix=" hrs"
                />
                <KPICard
                    title="Horas Extras"
                    value={asistenciaStats.total_horas_extras || 0}
                    subtitle="Este mes"
                    icon="fas fa-plus-circle"
                    color="orange"
                    suffix=" hrs"
                />
                <KPICard
                    title="Vacaciones Pendientes"
                    value={permisosVacaciones.vacaciones_pendientes || 0}
                    subtitle={`${permisosVacaciones.vacaciones_aprobadas || 0} aprobadas este mes`}
                    icon="fas fa-calendar-alt"
                    color="purple"
                />
            </div>

            {/* KPIs Row 2 - Secundarios */}
            <div className="kpi-grid">
                <KPICard
                    title="Permisos Este Mes"
                    value={permisosVacaciones.permisos_mes || 0}
                    subtitle="Permisos solicitados"
                    icon="fas fa-file-signature"
                    color="blue"
                />
                <motion.div
                    className="cumpleanos-container"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    whileHover={{ scale: 1.02 }}
                >
                    <div className="cumpleanos-card">
                        <div className="cumpleanos-header">
                            <div className="cumpleanos-icon">
                                <i className="fas fa-birthday-cake"></i>
                            </div>
                            <div className="cumpleanos-info">
                                <h3>Cumpleaños del mes</h3>
                                <p className="cumpleanos-subtitle">
                                    {cumpleanosMes.total > 0 ? `${cumpleanosMes.total} cumpleaños` : "Sin cumpleaños"}
                                </p>
                            </div>
                        </div>
                        <div className="cumpleanos-lista">
                            {cumpleanosMes.cumpleanos && cumpleanosMes.cumpleanos.length > 0 ? (
                                cumpleanosMes.cumpleanos.map((cumple, index) => (
                                    <div key={index} className="cumpleanos-item">
                                        <div className="cumpleanos-fecha">
                                            <span className="dia">{cumple.dia_cumpleanos}</span>
                                            <span className="fecha">{cumple.fecha_formateada}</span>
                                        </div>
                                        <div className="cumpleanos-nombre">
                                            <strong>{cumple.nombre_completo}</strong>
                                            <small>({cumple.edad} años)</small>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="no-cumpleanos">
                                    <i className="fas fa-calendar-times"></i>
                                    <span>No hay cumpleaños este mes</span>
                                </div>
                            )}
                        </div>
                    </div>
                </motion.div>
            </div>

            {/* Charts Row 1 */}
            <div className="charts-grid">
                {/* Asistencia Últimos Días */}
                <motion.div
                    className="chart-container"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                >
                    <div className="chart-header">
                        <h3><i className="fas fa-chart-line"></i> Asistencia Últimos 7 Días</h3>
                    </div>
                    <ResponsiveContainer width="100%" height={300}>
                        <AreaChart data={Array.isArray(asistenciaUltimosDias) ? asistenciaUltimosDias : []}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="fecha" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Area
                                type="monotone"
                                dataKey="registros"
                                stackId="1"
                                stroke="#8884d8"
                                fill="#8884d8"
                                name="Registros de Asistencia"
                            />
                            <Line
                                type="monotone"
                                dataKey="promedio_horas"
                                stroke="#82ca9d"
                                strokeWidth={2}
                                name="Promedio Horas"
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </motion.div>

                {/* Estado de Trabajadores Pie Chart */}
                <motion.div
                    className="chart-container"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                >
                    <div className="chart-header">
                        <h3><i className="fas fa-pie-chart"></i> Estado de Trabajadores</h3>
                    </div>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie
                                data={[
                                    { name: 'Activos', value: trabajadoresStats.trabajadores_activos || 0 },
                                    { name: 'Inactivos', value: trabajadoresStats.trabajadores_inactivos || 0 }
                                ]}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="value"
                            >
                                {[
                                    { name: 'Activos', value: trabajadoresStats.trabajadores_activos || 0 },
                                    { name: 'Inactivos', value: trabajadoresStats.trabajadores_inactivos || 0 }
                                ].map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip />
                        </PieChart>
                    </ResponsiveContainer>
                </motion.div>
            </div>

            {/* Charts Row 2 */}
            <div className="charts-grid">
                {/* Compensados del Mes */}
                <motion.div
                    className="chart-container"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.6 }}
                >
                    <div className="chart-header">
                        <h3><i className="fas fa-exchange-alt"></i> Compensados Este Mes</h3>
                    </div>
                    <ResponsiveContainer width="100%" height={300}>
                        {Array.isArray(compensadosMes) && compensadosMes.length > 0 ? (
                            <PieChart>
                                <Pie
                                    data={compensadosMes.map(item => ({
                                        ...item,
                                        TipoCompensado: item.TipoCompensado === 'ADD' ? 'Horas Adicionales' :
                                            item.TipoCompensado === 'DEL' ? 'Horas Descontadas' :
                                                item.TipoCompensado
                                    }))}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={({ TipoCompensado, percent }) => `${TipoCompensado} ${(percent * 100).toFixed(0)}%`}
                                    outerRadius={80}
                                    fill="#8884d8"
                                    dataKey="cantidad"
                                >
                                    {compensadosMes.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip
                                    formatter={(value, name, props) => [
                                        `${value} compensados`,
                                        props.payload.TipoCompensado === 'ADD' ? 'Horas Adicionales' :
                                            props.payload.TipoCompensado === 'DEL' ? 'Horas Descontadas' :
                                                props.payload.TipoCompensado
                                    ]}
                                    labelFormatter={() => 'Tipo de Compensado'}
                                />
                            </PieChart>
                        ) : (
                            <div className="no-data">
                                <i className="fas fa-info-circle"></i>
                                <p>No hay datos de compensados este mes</p>
                            </div>
                        )}
                    </ResponsiveContainer>
                </motion.div>

                {/* Actividad por Usuario - Top 5 */}
                <motion.div
                    className="chart-container"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.8 }}
                >
                    <div className="chart-header">
                        <h3><i className="fas fa-user-friends"></i> Top 5 Usuarios Más Activos</h3>
                    </div>
                    <ResponsiveContainer width="100%" height={300}>
                        {Array.isArray(actividadMensual) && actividadMensual.length > 0 ? (
                            <BarChart data={actividadMensual.slice(0, 5)}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="usuario"
                                    angle={-25}
                                    textAnchor="end"
                                    height={60}
                                />
                                <YAxis />
                                <Tooltip
                                    formatter={(value, name) => [value, 'Actividades']}
                                    labelFormatter={(label) => `Usuario: ${label}`}
                                />
                                <Bar dataKey="actividades" fill="#4e73df" />
                            </BarChart>
                        ) : (
                            <div className="no-data">
                                <i className="fas fa-info-circle"></i>
                                <p>No hay datos de actividad este mes</p>
                            </div>
                        )}
                    </ResponsiveContainer>
                </motion.div>
            </div>

            {/* Actividad del Sistema */}
            <motion.div
                className="chart-container full-width"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 1.0 }}
            >
                <div className="chart-header">
                    <h3><i className="fas fa-users-cog"></i> Actividad por Usuario Este Mes</h3>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={Array.isArray(actividadMensual) ? actividadMensual : []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                            dataKey="usuario"
                            angle={-45}
                            textAnchor="end"
                            height={80}
                            fontSize={12}
                        />
                        <YAxis />
                        <Tooltip
                            formatter={(value, name) => [value, 'Actividades realizadas']}
                            labelFormatter={(label) => `Usuario: ${label}`}
                        />
                        <Bar dataKey="actividades" fill="#FF8042" name="Actividades" />
                    </BarChart>
                </ResponsiveContainer>
            </motion.div>

            {/* Summary Cards */}
            <motion.div
                className="summary-grid"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 1.2 }}
            >
                <div className="summary-card">
                    <div className="summary-header">
                        <h4><i className="fas fa-exclamation-triangle"></i> Alertas y Notificaciones</h4>
                    </div>
                    <div className="summary-content">
                        <div className="alert-item">
                            <span className="alert-badge bg-warning">
                                {permisosVacaciones.vacaciones_pendientes || 0}
                            </span>
                            <span>Solicitudes de vacaciones pendientes</span>
                        </div>
                    </div>
                </div>

                <div className="summary-card">
                    <div className="summary-header">
                        <h4><i className="fas fa-trophy"></i> Indicadores de Rendimiento</h4>
                    </div>
                    <div className="summary-content">
                        <div className="performance-metric">
                            <span className="metric-label">Días promedio de vacaciones:</span>
                            <span className="metric-value">
                                {permisosVacaciones.vacaciones_aprobadas > 0 ?
                                    (permisosVacaciones.dias_vacaciones_mes / permisosVacaciones.vacaciones_aprobadas).toFixed(1) : 0} días
                            </span>
                        </div>
                        <div className="performance-metric">
                            <span className="metric-label">Horas extras por trabajador:</span>
                            <span className="metric-value">
                                {trabajadoresStats.trabajadores_activos > 0 ?
                                    (asistenciaStats.total_horas_extras / trabajadoresStats.trabajadores_activos).toFixed(1) : 0} hrs
                            </span>
                        </div>
                    </div>
                </div>
            </motion.div>

            <div className="dashboard-footer">
                <p className="text-muted">
                    <i className="fas fa-sync-alt"></i>
                    Última actualización: {new Date().toLocaleString()}
                </p>
                <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={fetchDashboardData}
                    disabled={loading}
                >
                    <i className="fas fa-refresh"></i> {loading ? 'Actualizando...' : 'Actualizar'}
                </button>
            </div>
        </div>
    );
};

export default Dashboard;
